// Cash denomination entry for the Collection Dashboard (one entry per user per IST day).
// Shown only when the facility has `cash_denomination_enabled` (collection_report summary).
// The server derives cash collected / refunded / net cash and re-validates every save, so
// the numbers here are only for live feedback.
import React, { useEffect, useMemo, useState } from "react";

// Accepted Indian denominations — the ₹1,000 note is withdrawn and intentionally absent.
export const CASH_NOTES = [2000, 500, 200, 100, 50, 20, 10, 5, 1];
export const CASH_COINS = [20, 10, 5, 2, 1];

const keyOf = (type, denomination) => `${type}-${denomination}`;
const QTY_TYPING = /^\d*$/;
const toQty = (raw) => (/^\d+$/.test(String(raw ?? "")) ? parseInt(raw, 10) : 0);

export const formatRupees = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "2026-10-13T08:15:00+00:00" -> "13 Oct 2026, 01:45 pm" in IST.
export const formatIstDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Today's calendar date in IST as YYYY-MM-DD (the server's day boundary). */
export const getIstDateStr = (date = new Date()) =>
  new Date(date.getTime() + 330 * 60 * 1000).toISOString().split("T")[0];

/** API denominations -> { "NOTE-500": "3", ... } */
export function valuesFromDenominations(list) {
  const values = {};
  (list || []).forEach((d) => {
    values[keyOf(d.denomination_type, d.denomination)] = String(d.quantity);
  });
  return values;
}

/** Only the denominations the user actually entered, in the API payload shape. */
export function buildDenominationsPayload(values) {
  const rows = [];
  [["NOTE", CASH_NOTES], ["COIN", CASH_COINS]].forEach(([type, list]) => {
    list.forEach((d) => {
      const quantity = toQty(values?.[keyOf(type, d)]);
      if (quantity > 0) rows.push({ type, denomination: d, quantity });
    });
  });
  return rows;
}

/** Sum of denomination × quantity. */
export function denominationTotal(values) {
  let total = 0;
  [["NOTE", CASH_NOTES], ["COIN", CASH_COINS]].forEach(([type, list]) => {
    list.forEach((d) => {
      total += d * toQty(values?.[keyOf(type, d)]);
    });
  });
  return total;
}

const inputStyle = {
  width: 70,
  padding: "4px 6px",
  borderRadius: 6,
  border: "1px solid #ced4da",
  textAlign: "right",
};

function DenominationGroup({ title, type, list, values, onChange, readOnly }) {
  return (
    <div style={{ flex: 1, minWidth: 240 }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: "#1c2c4c" }}>{title}</div>
      {list.map((d) => {
        const raw = values?.[keyOf(type, d)] ?? "";
        const qty = toQty(raw);
        if (readOnly && qty === 0) return null;
        return (
          <div key={d} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ width: 52 }}>₹{d}</span>
            <span>×</span>
            {readOnly ? (
              <span style={{ width: 70, textAlign: "right" }}>{qty}</span>
            ) : (
              <input
                type="text"
                inputMode="numeric"
                placeholder="0"
                value={raw}
                onChange={(e) => {
                  if (QTY_TYPING.test(e.target.value)) onChange(keyOf(type, d), e.target.value);
                }}
                style={inputStyle}
              />
            )}
            <span style={{ marginLeft: "auto", color: "#495057" }}>{formatRupees(d * qty)}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * One user's entry for one IST day. `entry` is an item of summary.cash_denomination_entries;
 * `onSave(entryDate, denominations)` must return a promise (rejects on failure).
 */
export default function CashDenominationEntryCard({ entry, onSave }) {
  const savedKey = JSON.stringify(entry.denominations);
  const [values, setValues] = useState(() => valuesFromDenominations(entry.denominations));
  const [saving, setSaving] = useState(false);

  // Re-sync when the server data changes (after a save, a refetch or a date change).
  useEffect(() => {
    setValues(valuesFromDenominations(entry.denominations));
  }, [savedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const readOnly = !entry.editable;
  // The denomination total has to equal the net cash (after refunds) to the nearest rupee.
  const expectedCash = Math.round(Number(entry.net_cash || 0));
  const total = useMemo(() => denominationTotal(values), [values]);
  const savedTotal = Number(entry.denomination_total || 0);
  const exceeds = total > expectedCash;
  const lessThan = total < expectedCash;
  const mismatch = exceeds || lessThan;
  const dirty = JSON.stringify(buildDenominationsPayload(values)) !==
    JSON.stringify(buildDenominationsPayload(valuesFromDenominations(entry.denominations)));
  const savedMismatch = !dirty && entry.is_mismatch;
  const canSave = !readOnly && !saving && dirty && total > 0 && !mismatch;

  const handleChange = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(entry.entry_date, buildDenominationsPayload(values));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        border: `1px solid ${entry.is_own ? "#bfdbfe" : "#e5e7eb"}`,
        borderRadius: 8,
        background: entry.is_own ? "#f8fbff" : "#fafafa",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 700, color: "#1c2c4c" }}>
          {entry.entry_date} — {entry.is_own ? `${entry.user_name} (You)` : entry.user_name}
        </div>
        <div style={{ fontSize: 12, color: "#6c757d" }}>
          {entry.has_entry ? `Last updated ${formatIstDateTime(entry.updated_at || entry.created_at)}` : "No entry yet"}
        </div>
      </div>

      <div style={{ fontSize: 13, color: "#495057", margin: "6px 0 10px" }}>
        Cash collected {formatRupees(entry.cash_collected)} − Cash refunded {formatRupees(entry.cash_refunded)} ={" "}
        <b>Net cash {formatRupees(entry.net_cash)}</b>
      </div>

      {readOnly && !entry.has_entry ? (
        <div style={{ fontSize: 13, color: "#6c757d" }}>No cash denomination recorded.</div>
      ) : (
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <DenominationGroup title="Notes" type="NOTE" list={CASH_NOTES} values={values} onChange={handleChange} readOnly={readOnly} />
          <DenominationGroup title="Coins" type="COIN" list={CASH_COINS} values={values} onChange={handleChange} readOnly={readOnly} />
        </div>
      )}

      <div
        style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: "1px solid #dbeafe",
          display: "flex",
          justifyContent: "space-between",
          fontWeight: 600,
          color: (!readOnly && total > 0 && mismatch) || savedMismatch ? "#d32f2f" : "#1c2c4c",
        }}
      >
        <span>Denomination total</span>
        <span>{formatRupees(readOnly ? savedTotal : total)}</span>
      </div>

      {!readOnly && total > 0 && mismatch && (
        <div style={{ marginTop: 4, fontSize: 12, color: "#d32f2f" }}>
          The total cash denomination amount ({formatRupees(total)}) {exceeds ? "exceeds" : "is less than"} the actual
          cash collected after refunds ({formatRupees(expectedCash)}). Please adjust the denomination quantities
          before saving.
        </div>
      )}
      {savedMismatch && (
        <div style={{ marginTop: 4, fontSize: 12, color: "#d32f2f" }}>
          The saved denomination total ({formatRupees(savedTotal)}) no longer matches the net cash after refunds (
          {formatRupees(entry.expected_cash)}); it is {entry.over_limit ? "more" : "less"} by{" "}
          {formatRupees(entry.over_limit ? entry.excess : entry.shortfall)}.
          {entry.editable ? " Please update the entry." : ""}
        </div>
      )}

      {!readOnly && (
        <div style={{ marginTop: 10, textAlign: "right" }}>
          <button
            type="button"
            className="btn btn__secondary btn__rounded"
            disabled={!canSave}
            onClick={handleSave}
            title={mismatch && total > 0 ? "Denomination total must equal the net cash" : undefined}
          >
            {saving ? "Saving..." : entry.has_entry ? "Update" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Compact dashboard row for one user's day. The button opens the entry in a modal
 * (`onOpen(entry)`): add/update for the signed-in user's own days, view for everyone else's.
 */
export function CashDenominationEntrySummary({ entry, onOpen }) {
  const buttonLabel = entry.editable ? (entry.has_entry ? "Update Denomination" : "Add Denomination") : "View";
  return (
    <div
      style={{
        marginTop: 10,
        padding: "10px 12px",
        border: `1px solid ${entry.is_own ? "#bfdbfe" : "#e5e7eb"}`,
        borderRadius: 8,
        background: entry.is_own ? "#f8fbff" : "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 260 }}>
        <div style={{ fontWeight: 700, color: "#1c2c4c" }}>
          {entry.entry_date} — {entry.is_own ? `${entry.user_name} (You)` : entry.user_name}
        </div>
        <div style={{ fontSize: 13, color: "#495057", marginTop: 2 }}>
          Cash collected {formatRupees(entry.cash_collected)} − Cash refunded {formatRupees(entry.cash_refunded)} ={" "}
          <b>Net cash {formatRupees(entry.net_cash)}</b>
        </div>
        <div style={{ fontSize: 12, color: "#6c757d", marginTop: 2 }}>
          {entry.has_entry
            ? `Denominated ${formatRupees(entry.denomination_total)} · Last updated ${formatIstDateTime(entry.updated_at || entry.created_at)}`
            : "No denomination entry yet"}
        </div>
        {entry.is_mismatch && (
          <div style={{ fontSize: 12, color: "#d32f2f", marginTop: 2 }}>
            Denomination total {entry.over_limit ? "exceeds" : "is less than"} net cash after refunds by{" "}
            {formatRupees(entry.over_limit ? entry.excess : entry.shortfall)}.
            {entry.editable ? " Please update the entry." : ""}
          </div>
        )}
      </div>
      <button type="button" className="btn btn__secondary btn__rounded" onClick={() => onOpen(entry)}>
        {buttonLabel}
      </button>
    </div>
  );
}
